/**
 * Eigene Seiten (S50) in the app: uploading one, fetching their files for the PDF and Word of a menu.
 * A file never changes (its name is its content's hash), so a file once fetched is kept for good,
 * in memory while the app runs; the browser keeps it besides (cache-control immutable).
 */
import { OWN_PAGE_LIMITS, ownPagesOf } from "@speisekarte/core";
import type { MenuOwnPages, OwnPage, OwnPageKind } from "@speisekarte/core";
import { ApiError, api, store } from "./store.ts";
import type { Data } from "./store.ts";

/** A PDF into the collection, as a page of this name and kind. */
export async function uploadOwnPage(file: Blob, name: string, kind: OwnPageKind): Promise<OwnPage> {
  if (file.size > OWN_PAGE_LIMITS.bytes) throw new ApiError(413, `Die Datei ist zu groß (höchstens ${OWN_PAGE_LIMITS.bytes / 1_000_000} MB).`);
  const r = await store.change(() => api<{ page: OwnPage }>(`/api/own-pages?name=${encodeURIComponent(name)}&kind=${kind}`, { pdf: file, timeout: 300_000 }));
  return r.page;
}

const held = new Map<string, Promise<Uint8Array>>();

/** The bytes of a page's file, fetched once. */
export function ownPageBytes(page: Pick<OwnPage, "file">): Promise<Uint8Array> {
  let p = held.get(page.file);
  if (!p) {
    const workspace = store.session?.workspaceId;
    p = fetch(`/api/own-pages/file/${page.file}`, { credentials: "same-origin", headers: workspace ? { "x-speisekarte-workspace": workspace } : {} }).then(async (res) => {
      if (!res.ok) throw new ApiError(res.status, "Eine eigene Seite ließ sich nicht laden. Bitte mit Netz noch einmal.");
      return new Uint8Array(await res.arrayBuffer());
    });
    p.catch(() => held.delete(page.file));
    held.set(page.file, p);
  }
  return p;
}

/** The files of the pages a menu takes, before and after it; none for an insert (S37). */
export async function ownPageFiles(menu: { ownPages?: MenuOwnPages }, data: Data): Promise<{ front: Uint8Array[]; back: Uint8Array[] }> {
  const { front, back } = ownPagesOf(menu, data.ownPages ?? []);
  return { front: await Promise.all(front.map(ownPageBytes)), back: await Promise.all(back.map(ownPageBytes)) };
}
